import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowRight, ArrowLeft, Hotel, MapPin, Database, Target, Check } from 'lucide-react';
import { AuthPageLayout } from '@/components/auth/AuthPageLayout';
import { cn } from '@/lib/utils';

const TOTAL_STEPS = 4;

const LIGHT_SELECT_CONTENT =
  'z-[200] max-h-[min(22rem,70vh)] border border-vesta-navy/10 bg-white text-vesta-navy shadow-xl';
const LIGHT_SELECT_ITEM =
  'cursor-pointer py-2.5 pl-8 pr-3 text-vesta-navy focus:bg-vesta-mist/40 data-[highlighted]:bg-vesta-mist/40 data-[state=checked]:bg-vesta-mist/25';

/**
 * Stored on `hotels.onboarding_profile` (jsonb). Track C may promote comp_set_names to comp_properties.
 *
 * @example
 * {
 *   role: "owner" | "gm" | "controller" | "asset_manager" | "finance" | "revenue" | "other",
 *   portfolio_size_bucket: "1" | "2_5" | "6_15" | "16_plus",
 *   primary_goals: string[],
 *   data_approach: "pms_live" | "pms_export" | "accounting" | "mixed" | "not_sure",
 *   comp_set_names?: string[],
 *   notes?: string,
 *   onboarding_version: 2,
 *   completed_at: string
 * }
 */
interface OnboardingFormData {
  hotelName: string;
  propertyType: string;
  roomCount: string;
  addressLine: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  timezone: string;
  currency: string;
  role: string;
  portfolioSize: string;
  primaryGoals: string[];
  dataApproach: string;
  pmsProvider: string;
  notes: string;
}

const PROPERTY_TYPES = [
  { value: 'independent', label: 'Independent' },
  { value: 'boutique', label: 'Boutique' },
  { value: 'chain', label: 'Chain / flagged' },
  { value: 'resort', label: 'Resort' },
  { value: 'extended_stay', label: 'Extended stay' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'serviced_apartment', label: 'Serviced apartments' },
] as const;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (Paris)' },
  { value: 'Asia/Dubai', label: 'Gulf (Dubai)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
] as const;

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
] as const;

const PMS_PROVIDERS = [
  { value: 'mews', label: 'Mews' },
  { value: 'cloudbeds', label: 'Cloudbeds' },
  { value: 'opera', label: 'Oracle OPERA' },
  { value: 'apaleo', label: 'Apaleo' },
  { value: 'protel', label: 'Protel' },
  { value: 'littlehotelier', label: 'Little Hotelier' },
  { value: 'other', label: 'Other PMS' },
  { value: 'none', label: 'Not connected yet — CSV / manual first' },
] as const;

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner / partner' },
  { value: 'gm', label: 'General manager' },
  { value: 'asset_manager', label: 'Asset / portfolio manager' },
  { value: 'finance', label: 'Finance / controller' },
  { value: 'revenue', label: 'Revenue manager' },
  { value: 'other', label: 'Other' },
] as const;

const PORTFOLIO_OPTIONS = [
  { value: '1', label: 'This property only' },
  { value: '2_5', label: '2–5 properties' },
  { value: '6_15', label: '6–15 properties' },
  { value: '16_plus', label: '16+ properties' },
] as const;

const GOAL_OPTIONS = [
  { id: 'daily_briefing', label: 'Daily AI briefings', description: 'Morning readouts on what changed and what needs attention.' },
  { id: 'anomalies', label: 'Anomaly & cost alerts', description: 'Catch unusual spend, revenue drops, and margin pressure.' },
  { id: 'reporting', label: 'Owner / lender reporting', description: 'Package performance updates for owners, lenders, or partners.' },
  { id: 'benchmarking', label: 'Benchmarking vs market', description: 'Compare ADR, occupancy, and RevPAR against your comp set.' },
  { id: 'forecasting', label: 'Forecasting & scenarios', description: 'Model future performance and test operating decisions.' },
  { id: 'marketplace', label: 'Vendor / cost savings', description: 'Find savings opportunities across supplies and services.' },
] as const;

const DATA_APPROACH_OPTIONS = [
  { value: 'pms_live', label: 'Live PMS API / integration' },
  { value: 'pms_export', label: 'PMS exports (CSV / Excel)' },
  { value: 'accounting', label: 'Accounting system (QuickBooks, …)' },
  { value: 'mixed', label: 'Mix of PMS + accounting + spreadsheets' },
  { value: 'not_sure', label: 'Not sure yet — need guidance' },
] as const;

const HotelOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState<OnboardingFormData>({
    hotelName: '',
    propertyType: '',
    roomCount: '',
    addressLine: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    timezone: '',
    currency: '',
    role: '',
    portfolioSize: '',
    primaryGoals: [],
    dataApproach: '',
    pmsProvider: '',
    notes: '',
  });

  useEffect(() => {
    document.title = 'Set up your property · Vesta CFO';
  }, []);

  const set = <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleGoal = (id: string) => {
    setForm((prev) => ({
      ...prev,
      primaryGoals: prev.primaryGoals.includes(id)
        ? prev.primaryGoals.filter((g) => g !== id)
        : [...prev.primaryGoals, id],
    }));
  };

  const roomsNum = parseInt(form.roomCount, 10);
  const canProceedStep1 =
    form.hotelName.trim() !== '' && form.propertyType !== '' && !Number.isNaN(roomsNum) && roomsNum >= 1;
  const canProceedStep2 =
    form.city.trim() !== '' && form.country.trim() !== '' && form.timezone !== '' && form.currency !== '';
  const canProceedStep3 =
    form.role !== '' && form.portfolioSize !== '' && form.primaryGoals.length > 0 && form.dataApproach !== '';
  const canComplete = form.pmsProvider !== '';

  const handleComplete = async () => {
    if (!user) return;
    if (!canComplete) {
      toast.error('Please select how you use a PMS (or choose “not connected yet”).');
      return;
    }
    setIsSubmitting(true);
    try {
      let orgId = localStorage.getItem('vesta_onboarding_org_id');

      if (!orgId) {
        const emailDomain = (user.email ?? '').split('@')[1] ?? '';
        const orgName = emailDomain
          ? emailDomain.split('.')[0].charAt(0).toUpperCase() + emailDomain.split('.')[0].slice(1) + ' Hotel Group'
          : 'My Hotel Group';
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .insert({ name: orgName, owner_user_id: user.id, plan: 'starter' })
          .select('id')
          .single();
        if (orgError) throw orgError;
        orgId = orgData.id;
      }

      const onboarding_profile = {
        role: form.role,
        portfolio_size_bucket: form.portfolioSize,
        primary_goals: form.primaryGoals,
        data_approach: form.dataApproach,
        notes: form.notes.trim() || undefined,
        onboarding_version: 2,
        completed_at: new Date().toISOString(),
      };

      const pmsVal = form.pmsProvider === 'none' ? null : form.pmsProvider;

      const { data: hotelData, error: hotelError } = await supabase
        .from('hotels')
        .insert({
          organization_id: orgId,
          name: form.hotelName.trim(),
          property_type: form.propertyType,
          room_count: roomsNum,
          address: form.addressLine.trim() || null,
          city: form.city.trim(),
          state: form.state.trim() || null,
          zip: form.zip.trim() || null,
          country: form.country.trim(),
          timezone: form.timezone,
          currency: form.currency,
          pms_provider: pmsVal,
          onboarding_profile,
        })
        .select('id')
        .single();

      if (hotelError) throw hotelError;

      const { error: memberError } = await supabase.from('hotel_members').insert({
        hotel_id: hotelData.id,
        user_id: user.id,
        role: 'owner',
      });

      if (memberError) throw memberError;

      localStorage.removeItem('vesta_onboarding_org_id');
      localStorage.removeItem('vesta_pending_google_signup');

      toast.success('Property saved. Welcome to Vesta CFO.');
      navigate('/dashboard');
    } catch (err: unknown) {
      console.error('[HotelOnboarding] Error:', err);
      const message = err instanceof Error ? err.message : 'Failed to save your hotel. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressValue = (step / TOTAL_STEPS) * 100;

  const stepIcons = [
    <Hotel key="hotel" className="w-5 h-5" />,
    <MapPin key="map" className="w-5 h-5" />,
    <Target key="target" className="w-5 h-5" />,
    <Database key="db" className="w-5 h-5" />,
  ];

  const stepTitles = ['Property basics', 'Location', 'Goals & data', 'Systems & finish'];

  const stepBlocked =
    (step === 1 && !canProceedStep1) ||
    (step === 2 && !canProceedStep2) ||
    (step === 3 && !canProceedStep3) ||
    (step === 4 && !canComplete);

  const getStepHelp = () => {
    if (!stepBlocked) return null;
    if (step === 3) {
      if (!form.role) return 'Select your role to continue.';
      if (!form.portfolioSize) return 'Select how many properties you oversee.';
      if (form.primaryGoals.length === 0) return 'Select at least one priority.';
      if (!form.dataApproach) return 'Select how you get financial or PMS data today.';
    }
    return null;
  };

  const stepHelp = getStepHelp();

  return (
    <AuthPageLayout>
      <section className="onboarding-page">
        <div className="mb-6 w-full max-w-lg mx-auto">
          <div className="mb-3 flex items-center justify-between gap-4">
            <span className="whitespace-nowrap text-sm font-medium text-[var(--rd-text-2)]">
              Step {step} of {TOTAL_STEPS}
            </span>
            <span className="truncate text-right text-sm font-medium text-[var(--vesta-navy)]">{stepTitles[step - 1]}</span>
          </div>
          <Progress value={progressValue} className="h-1 bg-vesta-mist/50 [&>div]:bg-vesta-gold" />
        </div>

        <div className="onboarding-card">
        <Card className="border-0 shadow-none bg-transparent p-0">
        <CardHeader className="pb-4">
          <div className="mb-1 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-vesta-gold/15 text-vesta-gold">
              {stepIcons[step - 1]}
            </div>
            <CardTitle className="text-xl text-vesta-navy">{stepTitles[step - 1]}</CardTitle>
          </div>
          <CardDescription className="text-vesta-navy/80">
            {step === 1 && 'Official name, type, and room count for this property.'}
            {step === 2 && 'Where you operate and how we should format numbers.'}
            {step === 3 && 'Helps us prioritize alerts, integrations, and your dashboard.'}
            {step === 4 && 'Primary PMS (if any). You can connect or import data next from Integrations.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {step === 1 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Hotel name *</label>
                <Input
                  placeholder="e.g. The Grand Meridian"
                  value={form.hotelName}
                  onChange={(e) => set('hotelName', e.target.value)}
                  className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Property type *</label>
                <Select value={form.propertyType} onValueChange={(v) => set('propertyType', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {PROPERTY_TYPES.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value} className={LIGHT_SELECT_ITEM}>
                        {pt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Number of keys / rooms *</label>
                <Input
                  type="number"
                  min={1}
                  placeholder="e.g. 120"
                  value={form.roomCount}
                  onChange={(e) => set('roomCount', e.target.value)}
                  className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Street address (optional)</label>
                <Input
                  placeholder="123 Ocean Drive"
                  value={form.addressLine}
                  onChange={(e) => set('addressLine', e.target.value)}
                  className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-vesta-navy/90">City *</label>
                  <Input
                    placeholder="Dubai"
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-vesta-navy/90">State / region (optional)</label>
                  <Input
                    placeholder="Dubai"
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-vesta-navy/90">Postal code (optional)</label>
                  <Input
                    placeholder="00000"
                    value={form.zip}
                    onChange={(e) => set('zip', e.target.value)}
                    className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-vesta-navy/90">Country *</label>
                  <Input
                    placeholder="United Arab Emirates"
                    value={form.country}
                    onChange={(e) => set('country', e.target.value)}
                    className="border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Timezone *</label>
                <Select value={form.timezone} onValueChange={(v) => set('timezone', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value} className={LIGHT_SELECT_ITEM}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Reporting currency *</label>
                <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value} className={LIGHT_SELECT_ITEM}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Your role *</label>
                <Select value={form.role} onValueChange={(v) => set('role', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value} className={LIGHT_SELECT_ITEM}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Portfolio size *</label>
                <Select value={form.portfolioSize} onValueChange={(v) => set('portfolioSize', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="How many properties do you oversee?" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {PORTFOLIO_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value} className={LIGHT_SELECT_ITEM}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-vesta-navy/90">What will you use Vesta for first? *</label>
                  <p className="mt-1 text-xs leading-relaxed text-vesta-navy-muted">
                    Select one or more. This customizes your dashboard and recommendations; it does not limit access.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {GOAL_OPTIONS.map((g) => {
                    const on = form.primaryGoals.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleGoal(g.id)}
                        className={cn(
                          'flex min-h-[92px] items-start gap-3 rounded-xl border p-3 text-left transition-colors',
                          on
                            ? 'border-vesta-gold bg-vesta-gold/15 text-vesta-navy shadow-sm'
                            : 'border-vesta-navy/10 bg-white text-vesta-navy/90 hover:border-vesta-gold/50 hover:bg-vesta-gold/5'
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                            on
                              ? 'border-vesta-gold bg-vesta-gold text-vesta-navy'
                              : 'border-vesta-navy/20 bg-vesta-mist/30 text-transparent'
                          )}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold">{g.label}</span>
                          <span className="mt-1 block text-xs leading-relaxed text-vesta-navy/65">{g.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-vesta-navy-muted">
                  {form.primaryGoals.length > 0
                    ? `${form.primaryGoals.length} selected`
                    : 'Choose at least one priority to continue.'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">How do you get financial / PMS data today? *</label>
                <Select value={form.dataApproach} onValueChange={(v) => set('dataApproach', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select the closest option" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {DATA_APPROACH_OPTIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value} className={LIGHT_SELECT_ITEM}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Primary PMS *</label>
                <Select value={form.pmsProvider} onValueChange={(v) => set('pmsProvider', v)}>
                  <SelectTrigger className="border border-vesta-navy/10 bg-white text-vesta-navy focus:ring-vesta-gold/35 [&>span]:text-vesta-navy">
                    <SelectValue placeholder="Select your system" />
                  </SelectTrigger>
                  <SelectContent className={LIGHT_SELECT_CONTENT}>
                    {PMS_PROVIDERS.map((p) => (
                      <SelectItem key={p.value} value={p.value} className={LIGHT_SELECT_ITEM}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-vesta-navy/90">Anything else we should know? (optional)</label>
                <Textarea
                  placeholder="e.g. We use a channel manager, fiscal year ends in March, multi-currency deposits…"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={4}
                  className="resize-none border border-vesta-navy/10 bg-white text-vesta-navy placeholder:text-vesta-navy-muted focus-visible:ring-vesta-gold/35"
                />
              </div>

              <div className="rounded-lg border border-vesta-gold/25 bg-vesta-gold/10 px-4 py-3">
                <p className="text-sm leading-relaxed text-vesta-navy/90">
                  After setup, open <span className="font-medium text-vesta-navy">Integrations</span> to connect your PMS, upload
                  CSVs, or enter manual metrics. You can change PMS in Settings later.
                </p>
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-2 gap-3">
            {step > 1 ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep((s) => s - 1)}
                className="shrink-0 text-vesta-navy/80 hover:bg-vesta-mist/40 hover:text-vesta-navy"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < TOTAL_STEPS ? (
              <Button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={stepBlocked}
                className="shrink-0 bg-vesta-gold font-semibold text-vesta-navy hover:bg-vesta-gold/90 disabled:opacity-40"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleComplete}
                disabled={isSubmitting || !canComplete}
                className="shrink-0 bg-vesta-gold font-semibold text-vesta-navy hover:bg-vesta-gold/90 disabled:opacity-40"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    Complete setup
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
          {stepHelp && (
            <p className="text-right text-xs font-medium text-vesta-navy-muted">{stepHelp}</p>
          )}
        </CardContent>
      </Card>
        </div>

      <p className="mt-6 max-w-md mx-auto text-center text-xs text-[var(--rd-text-3)]">
        Vesta CFO — encrypted setup. By continuing you confirm information is accurate for your property.
      </p>
      </section>
    </AuthPageLayout>
  );
};

export default HotelOnboarding;
